import React, { useState } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Table,
  ActionIcon,
  Badge,
  Text,
  TextInput,
  LoadingOverlay,
  Tooltip,
  Pagination,
  Select,
  Stack,
  Flex,
  Card,
  Menu,
  rem,
  Avatar,
  Progress,
  Alert,
  Modal,
  Tabs,
} from '@mantine/core';
import {
  IconSearch,
  IconActivity,
  IconEye,
  IconCheck,
  IconX,
  IconDots,
  IconCalendar,
  IconCamera,
  IconAlertTriangle,
  IconRefresh,
  IconTarget,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useDetections, useVerifyDetection } from '../../hooks/useDetections';
// import { useStudents } from '../../hooks/useStudents';
// import { useCameras } from '../../hooks/useCameras';
import type { Detection } from '../../types/api';

const Activities: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState('25');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activityTypeFilter, setActivityTypeFilter] = useState<string>('');
  const [verificationStatusFilter, setVerificationStatusFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('today');

  const [selectedDetection] = useState<Detection | null>(null);
  const [detailModalOpened, { close: closeDetailModal }] = useDisclosure(false);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedDetections, setSelectedDetections] = useState<string[]>([]);

  const {
    data: detectionsData,
    isLoading,
    error,
    refetch
  } = useDetections({
    page,
    per_page: parseInt(perPage),
    search: debouncedSearch,
    sort_by: sortBy,
    sort_order: sortOrder,
    filters: {
      ...(activityTypeFilter && { activity_type: activityTypeFilter }),
      ...(verificationStatusFilter && { verification_status: verificationStatusFilter }),
      ...(dateFilter && dateFilter !== 'all' && { date_filter: dateFilter }),
    },
  });

  // const { data: studentsData } = useStudents({ view: 'summary' });
  // const { data: camerasData } = useCameras({ view: 'summary' });

  const verifyDetectionMutation = useVerifyDetection();

  const detections = detectionsData?.detections || [];
  const pagination = detectionsData?.pagination;

  const handleVerifyDetection = async (detection: Detection, status: 'verified' | 'rejected' | 'needs_review') => {
    try {
      await verifyDetectionMutation.mutateAsync({
        id: detection.id,
        status: status,
        notes: '',
      });
      notifications.show({
        title: 'Success',
        message: `Detection ${status === 'verified' ? 'verified' : status === 'rejected' ? 'rejected' : 'marked for review'}`,
        color: status === 'verified' ? 'green' : status === 'rejected' ? 'red' : 'orange',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update detection status',
        color: 'red',
      });
    }
  };

  const handleBatchVerify = async (status: 'verified' | 'rejected' | 'needs_review') => {
    try {
      const promises = selectedDetections.map(id =>
        verifyDetectionMutation.mutateAsync({
          id,
          status: status,
          notes: '',
        })
      );
      await Promise.all(promises);

      notifications.show({
        title: 'Success',
        message: `${selectedDetections.length} detections ${status}`,
        color: status === 'verified' ? 'green' : status === 'rejected' ? 'red' : 'orange',
      });

      setSelectedDetections([]);
      setBatchMode(false);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update detections',
        color: 'red',
      });
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getActivityTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'entry': 'green',
      'exit': 'blue',
      'play': 'violet',
      'sleep': 'indigo',
      'meal': 'orange',
      'safety_concern': 'red',
      'incident': 'red',
      'unknown': 'gray',
    };
    return colors[type] || 'gray';
  };

  const getVerificationStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'verified': 'green',
      'rejected': 'red',
      'needs_review': 'orange',
      'pending': 'blue',
    };
    return colors[status] || 'gray';
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const toggleDetectionSelection = (detectionId: string) => {
    setSelectedDetections(prev =>
      prev.includes(detectionId)
        ? prev.filter(id => id !== detectionId)
        : [...prev, detectionId]
    );
  };

  if (error) {
    notifications.show({
      title: 'Error',
      message: 'Failed to load activities. Please try again.',
      color: 'red',
    });
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Activity Feed</Title>
          <Text size="sm" c="dimmed">
            Real-time student activities and AI detections
          </Text>
        </div>
        <Group>
          <Button
            variant="outline"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant={batchMode ? 'filled' : 'outline'}
            leftSection={<IconTarget size={16} />}
            onClick={() => setBatchMode(!batchMode)}
          >
            Batch Mode
          </Button>
        </Group>
      </Group>

      {/* Batch Actions */}
      {batchMode && selectedDetections.length > 0 && (
        <Alert color="blue" title={`${selectedDetections.length} detections selected`}>
          <Group>
            <Button
              size="xs"
              color="green"
              onClick={() => handleBatchVerify('verified')}
              loading={verifyDetectionMutation.isPending}
            >
              Verify All
            </Button>
            <Button
              size="xs"
              color="red"
              onClick={() => handleBatchVerify('rejected')}
              loading={verifyDetectionMutation.isPending}
            >
              Reject All
            </Button>
            <Button
              size="xs"
              color="orange"
              onClick={() => handleBatchVerify('needs_review')}
              loading={verifyDetectionMutation.isPending}
            >
              Needs Review
            </Button>
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                setSelectedDetections([]);
                setBatchMode(false);
              }}
            >
              Cancel
            </Button>
          </Group>
        </Alert>
      )}

      {/* Filters and Search */}
      <Card>
        <Stack gap="md">
          <Group>
            <TextInput
              placeholder="Search activities..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flexGrow: 1, maxWidth: 400 }}
            />
            <Select
              placeholder="Activity type"
              leftSection={<IconActivity size={16} />}
              data={[
                { value: '', label: 'All Types' },
                { value: 'entry', label: 'Entry' },
                { value: 'exit', label: 'Exit' },
                { value: 'play', label: 'Play' },
                { value: 'sleep', label: 'Sleep' },
                { value: 'meal', label: 'Meal' },
                { value: 'safety_concern', label: 'Safety Concern' },
                { value: 'incident', label: 'Incident' },
              ]}
              value={activityTypeFilter}
              onChange={(value) => setActivityTypeFilter(value || '')}
              style={{ minWidth: 150 }}
            />
            <Select
              placeholder="Verification status"
              leftSection={<IconCheck size={16} />}
              data={[
                { value: '', label: 'All Status' },
                { value: 'pending', label: 'Pending' },
                { value: 'verified', label: 'Verified' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'needs_review', label: 'Needs Review' },
              ]}
              value={verificationStatusFilter}
              onChange={(value) => setVerificationStatusFilter(value || '')}
              style={{ minWidth: 150 }}
            />
            <Select
              placeholder="Time period"
              leftSection={<IconCalendar size={16} />}
              data={[
                { value: 'today', label: 'Today' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'week', label: 'This Week' },
                { value: 'month', label: 'This Month' },
                { value: 'all', label: 'All Time' },
              ]}
              value={dateFilter}
              onChange={(value) => setDateFilter(value || 'today')}
              style={{ minWidth: 130 }}
            />
          </Group>

          <Group justify="flex-end">
            <Select
              label="Per page"
              value={perPage}
              onChange={(value) => {
                setPerPage(value || '25');
                setPage(1);
              }}
              data={[
                { value: '10', label: '10' },
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
              ]}
              style={{ width: 100 }}
            />
          </Group>
        </Stack>
      </Card>

      {/* Activities Table */}
      <Paper withBorder>
        <LoadingOverlay visible={isLoading} />

        {detections.length === 0 && !isLoading ? (
          <Flex direction="column" align="center" justify="center" py="xl">
            <IconActivity size={48} color="gray" />
            <Title order={4} c="dimmed" mt="md">
              No activities found
            </Title>
            <Text size="sm" c="dimmed" ta="center" mt="xs">
              {search || activityTypeFilter || verificationStatusFilter
                ? 'Try adjusting your search criteria'
                : 'Activity feed will appear here as events occur'}
            </Text>
          </Flex>
        ) : (
          <>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  {batchMode && <Table.Th style={{ width: 40 }}>Select</Table.Th>}
                  <Table.Th>Student & Activity</Table.Th>
                  <Table.Th>Detection Info</Table.Th>
                  <Table.Th>Location & Camera</Table.Th>
                  <Table.Th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('timestamp')}
                  >
                    Timestamp {getSortIcon('timestamp')}
                  </Table.Th>
                  <Table.Th>Confidence</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th style={{ width: 120 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {detections.map((detection: any) => (
                  <Table.Tr
                    key={detection.id}
                    style={{
                      backgroundColor: batchMode && selectedDetections.includes(detection.id)
                        ? 'var(--mantine-color-blue-0)'
                        : undefined
                    }}
                  >
                    {batchMode && (
                      <Table.Td>
                        <input
                          type="checkbox"
                          checked={selectedDetections.includes(detection.id)}
                          onChange={() => toggleDetectionSelection(detection.id)}
                        />
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar size="sm" radius="xl">
                          {detection.student ?
                            detection.student.first_name[0] + detection.student.last_name[0] :
                            '??'
                          }
                        </Avatar>
                        <div>
                          <Text fw={500} size="sm">
                            {detection.student ?
                              `${detection.student.first_name} ${detection.student.last_name}` :
                              'Unknown Student'
                            }
                          </Text>
                          <Group gap="xs" mt={2}>
                            <Badge
                              color={getActivityTypeColor(detection.activity_type)}
                              variant="light"
                              size="xs"
                            >
                              {detection.activity_type?.replace('_', ' ')}
                            </Badge>
                          </Group>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="xs">ID: {detection.id}</Text>
                        <Text size="xs" c="dimmed">
                          Safety Score: {detection.safety_score || 'N/A'}
                        </Text>
                        {detection.bounding_box && (
                          <Text size="xs" c="dimmed">
                            Bbox: {JSON.stringify(detection.bounding_box)}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Group gap={4}>
                          <IconCamera size={12} />
                          <Text size="xs">{detection.camera_name || 'Unknown Camera'}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {detection.area_name || 'No area'}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {formatTimestamp(detection.detected_at || detection.timestamp || '')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Progress
                          value={(detection.confidence || 0) * 100}
                          size="xs"
                          color={detection.confidence > 0.8 ? 'green' : detection.confidence > 0.6 ? 'yellow' : 'red'}
                        />
                        <Text size="xs" ta="center">
                          {((detection.confidence || 0) * 100).toFixed(1)}%
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={getVerificationStatusColor(detection.status || detection.alert_status)}
                        variant="light"
                        size="sm"
                      >
                        {(detection.status || detection.alert_status)?.replace('_', ' ') || 'Unknown'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Tooltip label="View details">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => navigate(`/admin/activities/detection/${detection.id}`)}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>

                        {detection.verification_status === 'pending' && (
                          <Menu shadow="md" width={200}>
                            <Menu.Target>
                              <ActionIcon variant="subtle" size="sm">
                                <IconDots size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconCheck style={{ width: rem(14), height: rem(14) }} />}
                                color="green"
                                onClick={() => handleVerifyDetection(detection, 'verified')}
                              >
                                Verify
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconX style={{ width: rem(14), height: rem(14) }} />}
                                color="red"
                                onClick={() => handleVerifyDetection(detection, 'rejected')}
                              >
                                Reject
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconAlertTriangle style={{ width: rem(14), height: rem(14) }} />}
                                color="orange"
                                onClick={() => handleVerifyDetection(detection, 'needs_review')}
                              >
                                Needs Review
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <Group justify="center" p="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={pagination.total_pages}
                  size="sm"
                />
              </Group>
            )}
          </>
        )}
      </Paper>

      {/* Summary Stats */}
      {pagination && (
        <Text size="sm" c="dimmed" ta="center">
          Showing {detections.length} of {pagination.total_count} activities
          {(search || activityTypeFilter || verificationStatusFilter) && ' (filtered)'}
        </Text>
      )}

      {/* Detection Detail Modal */}
      <Modal
        opened={detailModalOpened}
        onClose={closeDetailModal}
        title="Detection Details"
        size="lg"
      >
        {selectedDetection && (
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Title order={4}>
                  {selectedDetection.student ?
                    `${selectedDetection.student.first_name} ${selectedDetection.student.last_name}` :
                    'Unknown Student'
                  }
                </Title>
                <Text size="sm" c="dimmed">Detection ID: {selectedDetection.id}</Text>
              </div>
              <Badge
                color={getActivityTypeColor(selectedDetection.activity_type || '')}
                size="lg"
              >
                {selectedDetection.activity_type?.replace('_', ' ') || 'Unknown'}
              </Badge>
            </Group>

            <Tabs defaultValue="overview">
              <Tabs.List>
                <Tabs.Tab value="overview">Overview</Tabs.Tab>
                <Tabs.Tab value="technical">Technical Data</Tabs.Tab>
                <Tabs.Tab value="verification">Verification</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="overview" pt="md">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text size="sm">Timestamp:</Text>
                    <Text size="sm" fw={500}>
                      {selectedDetection.timestamp ? new Date(selectedDetection.timestamp).toLocaleString() : 'N/A'}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm">Camera:</Text>
                    <Text size="sm" fw={500}>
                      {selectedDetection.camera?.name || 'Unknown'}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm">Location:</Text>
                    <Text size="sm" fw={500}>
                      {selectedDetection.area_name || 'Unknown Area'}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm">Confidence:</Text>
                    <Text size="sm" fw={500}>
                      {((selectedDetection.confidence || 0) * 100).toFixed(1)}%
                    </Text>
                  </Group>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="technical" pt="md">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text size="sm">Safety Score:</Text>
                    <Text size="sm" fw={500}>
                      {selectedDetection.safety_score || 'N/A'}
                    </Text>
                  </Group>
                  {selectedDetection.bounding_box && (
                    <div>
                      <Text size="sm" mb="xs">Bounding Box:</Text>
                      <pre style={{ fontSize: '12px', background: 'var(--mantine-color-gray-0)', padding: '8px', borderRadius: '4px' }}>
                        {JSON.stringify(selectedDetection.bounding_box, null, 2)}
                      </pre>
                    </div>
                  )}
                  {selectedDetection.metadata && (
                    <div>
                      <Text size="sm" mb="xs">Metadata:</Text>
                      <pre style={{ fontSize: '12px', background: 'var(--mantine-color-gray-0)', padding: '8px', borderRadius: '4px' }}>
                        {JSON.stringify(selectedDetection.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="verification" pt="md">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text size="sm">Status:</Text>
                    <Badge color={getVerificationStatusColor(selectedDetection.verification_status || '')}>
                      {selectedDetection.verification_status?.replace('_', ' ')}
                    </Badge>
                  </Group>

                  {selectedDetection.verification_status === 'pending' && (
                    <Group>
                      <Button
                        color="green"
                        onClick={() => handleVerifyDetection(selectedDetection, 'verified')}
                        loading={verifyDetectionMutation.isPending}
                      >
                        Verify
                      </Button>
                      <Button
                        color="red"
                        onClick={() => handleVerifyDetection(selectedDetection, 'rejected')}
                        loading={verifyDetectionMutation.isPending}
                      >
                        Reject
                      </Button>
                      <Button
                        color="orange"
                        onClick={() => handleVerifyDetection(selectedDetection, 'needs_review')}
                        loading={verifyDetectionMutation.isPending}
                      >
                        Needs Review
                      </Button>
                    </Group>
                  )}

                  {selectedDetection.verified_at && (
                    <Group justify="space-between">
                      <Text size="sm">Verified At:</Text>
                      <Text size="sm" fw={500}>
                        {selectedDetection.verified_at ? new Date(selectedDetection.verified_at).toLocaleString() : 'Not verified'}
                      </Text>
                    </Group>
                  )}
                </Stack>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};

export default Activities;
